import type {
  DropdownSelectorSize,
  DropdownSelectorVariant,
} from './DropdownSelector.types';

/**
 * Map the trigger's size band onto the underlying `<Button>` size key, so the
 * chip inherits Button's height for that band:
 *   s (32px) → Button xs · m (44px) → Button l · l (48px) → Button xl ·
 *   xl (56px) → Button xxl
 */
export const DROPDOWN_TO_BUTTON_SIZE = {
  s: 'xs',
  m: 'l',
  l: 'xl',
  xl: 'xxl',
} as const;

/**
 * Per-size CONTAINER overrides applied on top of the underlying `<Button>`.
 * The Figma "Drop down selector" (section `1008:4306`) is a DISTINCT component
 * from Button — same bevel/palette, but a smaller corner radius, symmetric
 * content padding, and its own per-band gap. Verified per-node (Secondary /
 * Default):
 *
 *   size · height · radius(outerTop) · padding(symmetric) · gap · Figma node
 *   ─────────────────────────────────────────────────────────────────────────
 *   s   ·   32   ·   11   ·   8  ·  4  ·  1168:25112 / :25114
 *   m   ·   44   ·   15   ·  12  ·  8  ·  1156:16042 / :16044
 *   l   ·   48   ·   15   ·  14  ·  8  ·  1136:4864  / :4866
 *   xl  ·   56   ·   17   ·  16  ·  8  ·  1262:25403 / :25402
 *
 * Radius is applied uniformly (matching Button's post-bevel convention). Padding
 * folds the chip's 1-px border inset into the value (`px = N + 1`, i.e.
 * 9/13/15/17) so the flat Button — which keeps a 1-px `border` — reproduces the
 * Figma content inset; the folded values are exactly Button's own `pl` per band.
 */
export const DROPDOWN_TRIGGER_OVERRIDE: Record<DropdownSelectorSize, string> = {
  s: '!px-[9px] !gap-1 !rounded-[11px]',
  m: '!px-[13px] !gap-2 !rounded-[15px]',
  l: '!px-[15px] !gap-2 !rounded-[15px]',
  xl: '!px-[17px] !gap-2 !rounded-[17px]',
};

/**
 * Per-variant CONTAINER override for the RESTING (non-open) border. Only
 * `tertiary` needs it: Button's tertiary ships a lighter `secondary-100`
 * border, but the Figma selector's tertiary rests on `secondary-200`
 * (node `1075:16529`). Secondary already matches and ghost is borderless.
 * The open-state border is owned by {@link OPEN_OVERRIDE}.
 */
export const DROPDOWN_TRIGGER_VARIANT_OVERRIDE: Record<DropdownSelectorVariant, string> =
  {
    secondary: '',
    tertiary: '!border-secondary-200',
    ghost: '',
  };

/**
 * Leading-icon size (px) per band. Figma sizes the trigger's leading icon 4 px
 * LARGER than the chevron (s 16, m 22, l 24, xl 26 vs chevron 14/18/20/22 —
 * nodes `1262:25406` / `1136:4867` / `1156:16045` / `1168:25115`). Button sizes
 * both icon slots with one value, so the trigger wraps the leading icon to
 * force this larger size while the chevron keeps Button's size.
 */
export const DROPDOWN_TRIGGER_LEFT_ICON_SIZE: Record<DropdownSelectorSize, number> = {
  s: 16,
  m: 22,
  l: 24,
  xl: 26,
};

/**
 * Per-size TEXT overrides applied via Button's `textClassName`. Figma's "Drop
 * down selector" label ramp is Mona Sans **Medium** at every band — S = 12 / 20
 * (B3), M / L / XL = 18 / 22 (B4) — which does NOT line up with Button's per-size
 * type (xs 14, l 16, xl/xxl 18-SemiBold). Verified nodes: 1168:25116 (s),
 * 1156:16046 (m), 1136:4868 (l), 1262:25407 (xl).
 */
export const DROPDOWN_TRIGGER_TEXT_OVERRIDE: Record<DropdownSelectorSize, string> = {
  s: '!text-xs',
  m: '!text-lg',
  l: '!text-lg !font-medium native:!font-mona-medium',
  xl: '!font-medium native:!font-mona-medium',
};

/**
 * Per-variant CONTAINER overrides applied while the trigger is OPEN — it holds
 * Figma's "Opened" state for as long as the popup is showing. Verified against
 * the Opened nodes (`1075:15502` secondary, `1075:16585` tertiary, `1075:16870`
 * ghost):
 *   - secondary: fill `secondary-100`, border stays `secondary-200`, the bevel
 *     collapses to a symmetric 1-px border (`!border-b !pb-px`), shadow drops.
 *   - tertiary: fill `secondary-200`, border `secondary-300`.
 *   - ghost:    fill `secondary-200`, NO border (stays transparent).
 */
export const OPEN_OVERRIDE: Record<DropdownSelectorVariant, string> = {
  secondary: '!bg-secondary-100 !border-secondary-200 !border-b !pb-px !shadow-none ',
  tertiary: '!bg-secondary-200 !border-secondary-300 ',
  ghost: '!bg-secondary-200 !border-transparent ',
};
